import { Component } from '@theme/component';

/**
 * A custom element that formats rte content for easier styling
 */
class RTEFormatter extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.querySelectorAll('table').forEach(this.#formatTable);
    this.#labelVagueLinks();
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   */
  #formatTable(table) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('rte-table-wrapper');
    const parent = table.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }

  /**
   * Adds an accessible name to vague link text (here / this link / click here)
   * when the link does not already have an aria-label.
   */
  #labelVagueLinks() {
    const vague = /^(here|this link|click here)$/i;

    this.querySelectorAll('a[href]').forEach((link) => {
      if (!(link instanceof HTMLAnchorElement)) return;
      if (link.getAttribute('aria-label')) return;

      const text = (link.textContent || '').replace(/\s+/g, ' ').trim();
      if (!vague.test(text)) return;

      const title = (link.getAttribute('title') || '').trim();
      const titleIsUrl = /^https?:\/\//i.test(title);
      let label = title && !titleIsUrl ? title : '';

      if (!label) {
        try {
          label = this.#labelFromHref(link.href);
        } catch {
          return;
        }
      }

      if (label) link.setAttribute('aria-label', label);
    });
  }

  /**
   * Builds a readable name from the URL hash (same-page target) or last path segment.
   * @param {string} href
   * @returns {string}
   */
  #labelFromHref(href) {
    const url = new URL(href, window.location.origin);
    const hash = url.hash.replace(/^#/, '').trim();
    const segment = hash || url.pathname.split('/').filter(Boolean).pop() || '';
    return this.#humanizeSlug(segment);
  }

  /**
   * Decodes percent-encoded slugs and replaces separators with spaces.
   * @param {string} value
   * @returns {string}
   */
  #humanizeSlug(value) {
    let decoded = value.replace(/\+/g, ' ');
    try {
      decoded = decodeURIComponent(decoded);
    } catch {
      // Keep the plus-normalized value when the slug is malformed.
    }
    return decoded.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}
